import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {BettingContainerConfig} from "../betting-container.config";
import {WebsiteModuleState} from "../../../apps/website/models/website-module.state";
import {BettingZoneSiteSettingsDataSource} from "../../../domain/betting-zone/models/data-sources/site-settings/betting-zone.site-settings.data-source";
import {computed, makeObservable, observable} from "mobx";
import {SCREEN_RESOLUTION} from "../../../core/consts";
import {BETTING_CONTAINER} from "../betting-container-consts";
import {SportViewModel} from "../../../domain/sports/models/sport-view-model";
import {singleton} from "../../../infrastructure/decorators/singleton";
import {MainMenuViewModel} from "../../../domain/betting-zone/models/main-menu/main-menu.view-model";
import type {BettingZoneSeoBreadcrumbsModel} from "../../../domain/betting-zone/models/seo/breadcrumbs/betting-zone.seo.breadcrumbs.model";
import {THEME} from "../../../core/app/consts";

@singleton
export class BettingContainerState extends BaseModuleState {
  dataSource: BettingZoneSiteSettingsDataSource;
  @observable breadcrumbs: Array<BettingZoneSeoBreadcrumbsModel> = [];
  @observable activeButtonId: Number;
  lastShowOddsValue: Boolean = true;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, BettingContainerConfig, WebsiteModuleState];

  constructor(
      navigationManager: NavigationManager,
      appStore: AppStore,
      logger: LoggerUtils,
      datetimeUtils: DatetimeUtils,
      device: DeviceViewModel,
      termsUtils: TermsUtils,
      config: BettingContainerConfig,
      websiteModuleState: WebsiteModuleState,
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    this.websiteModuleState = websiteModuleState;

    makeObservable(this);
  }

  get isPopulated(): Boolean {
    return this.bettingZoneTerms;
  }

  @computed get siteHeaderButtons() {
    const buttons = this.dataSource?.mainMenu.map(button => new MainMenuViewModel(button, this.language))
    let maxItems: Number = buttons?.length;

    if (this.device.screenWidth >= SCREEN_RESOLUTION.lg_min && this.device.screenWidth < SCREEN_RESOLUTION.xl_min && this.buttonsLength > BETTING_CONTAINER.SITE_HEADER_BUTTONS.mediumBar) {
      maxItems = BETTING_CONTAINER.SITE_HEADER_BUTTONS.mediumBar - 1;
    } else if (this.device.screenWidth >= SCREEN_RESOLUTION.xl_min && this.buttonsLength > BETTING_CONTAINER.SITE_HEADER_BUTTONS.largeBar) {
      maxItems = BETTING_CONTAINER.SITE_HEADER_BUTTONS.largeBar - 1
    }

    return this.device.isSmallView ? buttons: buttons.slice(0, maxItems)
  }

  @computed get selectedMenuButton() {
    return this.dataSource?.mainMenu?.find(item => item.id === this.activeButtonId)
  }

  @computed get moreSiteHeaderButtons(): Array<SportViewModel> {
    const buttonsIds: Array<Number> = this.siteHeaderButtons?.map(item => item.id);
    const moreSiteHeaderButtons: Array<SportViewModel> = this.dataSource?.mainMenu?.filter(item => !buttonsIds.includes(item.id)).map(item => new MainMenuViewModel(item, this.language))

    return moreSiteHeaderButtons;
  }

  @computed get buttonsLength() {
    return this.dataSource?.mainMenu?.length;
  }

  get bettingZoneTerms() {
    return this.dataSource?.terms;
  }

  @computed get shouldRenderMoreButton(): Boolean {
    let shouldRenderMoreButton: Boolean = false;

    if (this.device.screenWidth >= SCREEN_RESOLUTION.lg_min && this.device.screenWidth < SCREEN_RESOLUTION.xl_min) {
      shouldRenderMoreButton = this.buttonsLength > BETTING_CONTAINER.SITE_HEADER_BUTTONS.mediumBar;
    } else if (this.device.screenWidth >= SCREEN_RESOLUTION.xl_min) {
      shouldRenderMoreButton = this.buttonsLength > BETTING_CONTAINER.SITE_HEADER_BUTTONS.largeBar;
    }

    return !this.device.isSmallView && shouldRenderMoreButton;
  }

  @computed get maxVisibleButtons(): Number {
    let maxItems: Number = this.buttonsLength;

    if (this.device.screenWidth >= SCREEN_RESOLUTION.lg_min && this.device.screenWidth < SCREEN_RESOLUTION.xl_min && this.buttonsLength > BETTING_CONTAINER.SITE_HEADER_BUTTONS.mediumBar) {
      maxItems = BETTING_CONTAINER.SITE_HEADER_BUTTONS.mediumBar - 1;
    } else if (this.device.screenWidth >= SCREEN_RESOLUTION.xl_min && this.buttonsLength > BETTING_CONTAINER.SITE_HEADER_BUTTONS.largeBar) {
      maxItems = BETTING_CONTAINER.SITE_HEADER_BUTTONS.largeBar - 1;
    }

    return maxItems;
  }

  @computed get isDarkMode(): Boolean {
    return this.appState.theme === THEME.DARK;
  }

  get publisherId(): Number {
    return this.dataSource?.publisherId;
  }
}
